import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../store/projectStore';
import { useUiStore } from '../store/uiStore';
import type { Project } from '../types';

// Summary shape returned by GET /api/projects (no nested applications/VMs)
type ProjectSummary = Pick<Project, 'id' | 'name' | 'clientName' | 'description' | 'createdAt' | 'updatedAt'>;

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  page: {
    padding: '32px 24px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '28px',
  },
  heading: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#E8EDF2',
    margin: 0,
  },
  subheading: {
    fontSize: '13px',
    color: '#8FA4B5',
    marginTop: '4px',
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '9px 16px',
    backgroundColor: '#F5A621',
    color: '#021526',
    border: 'none',
    borderRadius: '7px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '16px',
  },
  card: {
    backgroundColor: '#0d1f30',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '10px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  cardHover: {
    borderColor: 'rgba(245,166,33,0.5)',
  },
  cardName: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#E8EDF2',
    margin: 0,
  },
  cardClient: {
    fontSize: '12px',
    color: '#8FA4B5',
  },
  cardDescription: {
    fontSize: '12px',
    color: '#8FA4B5',
    marginTop: '2px',
    lineHeight: 1.5,
    display: '-webkit-box' as const,
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '12px',
  },
  cardDate: {
    fontSize: '11px',
    color: '#4C5C68',
  },
  cardActions: {
    display: 'flex',
    gap: '6px',
  },
  btnOpen: {
    padding: '4px 10px',
    backgroundColor: '#F5A621',
    color: '#021526',
    border: 'none',
    borderRadius: '5px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnDelete: {
    padding: '4px 10px',
    backgroundColor: 'rgba(220,60,60,0.15)',
    color: '#E05555',
    border: '1px solid rgba(220,60,60,0.3)',
    borderRadius: '5px',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnDuplicate: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    color: '#8FA4B5',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '5px',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '80px 24px',
    border: '1px dashed rgba(76,92,104,0.35)',
    borderRadius: '10px',
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#8FA4B5',
    marginBottom: '8px',
  },
  emptyBody: {
    fontSize: '13px',
    color: '#4C5C68',
    marginBottom: '24px',
    lineHeight: 1.6,
  },
  emptyBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '9px 18px',
    backgroundColor: '#F5A621',
    color: '#021526',
    border: 'none',
    borderRadius: '7px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  errorBanner: {
    padding: '10px 14px',
    backgroundColor: '#1e1010',
    border: '1px solid #7f1d1d',
    borderRadius: '7px',
    color: '#fca5a5',
    fontSize: '13px',
    marginBottom: '20px',
  },
  retryBtn: {
    marginLeft: '12px',
    padding: '3px 10px',
    backgroundColor: 'transparent',
    color: '#fca5a5',
    border: '1px solid #7f1d1d',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  skeletonBar: {
    backgroundColor: '#132840',
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  spinner: {
    width: '12px',
    height: '12px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    zIndex: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialog: {
    backgroundColor: '#0d1f30',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '12px',
    padding: '28px 32px',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  dialogTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#E8EDF2',
    margin: '0 0 8px',
  },
  dialogBody: {
    fontSize: '13px',
    color: '#8FA4B5',
    lineHeight: 1.6,
    margin: '0 0 24px',
  },
  dialogActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
  },
  btnCancel: {
    padding: '7px 16px',
    backgroundColor: 'transparent',
    color: '#8FA4B5',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnConfirmDelete: {
    padding: '7px 16px',
    backgroundColor: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnConfirmDuplicate: {
    padding: '7px 16px',
    backgroundColor: '#F5A621',
    color: '#021526',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    backgroundColor: '#0a1929',
    color: '#E8EDF2',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '20px',
    boxSizing: 'border-box' as const,
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function Dashboard() {
  const navigate = useNavigate();
  const setProject = useProjectStore(st => st.setProject);
  const clearProject = useProjectStore(st => st.clearProject);
  const setCurrentStep = useUiStore(st => st.setCurrentStep);
  const showToast = useUiStore(st => st.showToast);

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [duplicateSourceId, setDuplicateSourceId] = useState<string | null>(null);
  const [duplicateName, setDuplicateName] = useState('');

  useEffect(() => {
    void fetchProjects();
  }, []);

  async function fetchProjects() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/projects');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as ProjectSummary[];
      // Sort newest first
      data.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      setProjects(data);
    } catch {
      setError('Could not load projects. Make sure the app is running with Cloudflare Pages (npm run dev:cf).');
    } finally {
      setLoading(false);
    }
  }

  function handleNewProject() {
    setCreating(true);
    clearProject();
    setCurrentStep(1);
    navigate('/projects/new');
  }

  async function handleOpenProject(id: string) {
    setLoadingId(id);
    try {
      const res = await fetch(`/api/projects/${id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as Project;
      // Resolve active scenario: baseline, or first
      const activeScenarioId =
        data.activeScenarioId ??
        (data.scenarios.find(s => s.isBaseline) ?? data.scenarios[0])?.id;
      setProject({ ...data, activeScenarioId });
      setCurrentStep(1);
      navigate(`/projects/${id}`);
    } catch {
      setError(`Failed to load project. Please try again.`);
    } finally {
      setLoadingId(null);
    }
  }

  function handleDeleteClick(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    setConfirmDeleteId(id);
  }

  async function handleConfirmDelete() {
    const id = confirmDeleteId;
    if (!id) return;
    setConfirmDeleteId(null);
    setDeletingId(id);
    try {
      const res = await fetch(`/api/projects/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setProjects(prev => prev.filter(p => p.id !== id));
      showToast('Project deleted');
    } catch {
      setError('Failed to delete project. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }

  function handleDuplicateClick(e: React.MouseEvent, id: string, name: string) {
    e.stopPropagation();
    setDuplicateSourceId(id);
    setDuplicateName(`Copy of ${name}`);
  }

  async function handleConfirmDuplicate() {
    const id = duplicateSourceId;
    if (!id || !duplicateName.trim()) return;
    setDuplicateSourceId(null);
    setDuplicatingId(id);
    try {
      const res = await fetch(`/api/projects/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: duplicateName.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { id: string; name: string };
      showToast(`Duplicated as "${data.name}"`);
      // Navigate into the new project immediately
      const projectRes = await fetch(`/api/projects/${data.id}`);
      if (projectRes.ok) {
        const fullProject = (await projectRes.json()) as Project;
        const activeScenarioId =
          fullProject.activeScenarioId ??
          (fullProject.scenarios.find(s => s.isBaseline) ?? fullProject.scenarios[0])?.id;
        setProject({ ...fullProject, activeScenarioId });
        setCurrentStep(1);
        navigate(`/projects/${data.id}`);
      } else {
        // Fallback: refresh dashboard
        void fetchProjects();
      }
    } catch {
      setError('Failed to duplicate project. Please try again.');
    } finally {
      setDuplicatingId(null);
    }
  }

  function formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.heading}>Projects</h1>
          <p style={s.subheading}>All infrastructure sizing engagements</p>
        </div>
        <button
          type="button"
          style={{ ...s.btnPrimary, opacity: creating ? 0.8 : 1 }}
          data-primary-cta
          onClick={handleNewProject}
          disabled={creating}
        >
          {creating ? (
            <><span style={s.spinner} /> Creating…</>
          ) : '+ New Project'}
        </button>
      </div>

      {error && (
        <div style={s.errorBanner}>
          {error}
          <button
            type="button"
            style={s.retryBtn}
            onClick={() => void fetchProjects()}
          >
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div style={s.grid}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ ...s.card, cursor: 'default' }}>
              <div style={{ ...s.skeletonBar, height: '15px', width: '60%' }} />
              <div style={{ ...s.skeletonBar, height: '12px', width: '40%', marginTop: '8px' }} />
              <div style={{ ...s.skeletonBar, height: '11px', width: '90%', marginTop: '10px' }} />
              <div style={{ ...s.skeletonBar, height: '11px', width: '70%', marginTop: '5px' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                <div style={{ ...s.skeletonBar, height: '11px', width: '80px' }} />
                <div style={{ display: 'flex', gap: '6px' }}>
                  <div style={{ ...s.skeletonBar, height: '22px', width: '44px', borderRadius: '5px' }} />
                  <div style={{ ...s.skeletonBar, height: '22px', width: '60px', borderRadius: '5px' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && projects.length === 0 && !error && (
        <div style={s.empty}>
          <p style={s.emptyTitle}>No projects yet</p>
          <p style={s.emptyBody}>
            Create your first sizing engagement to get started.<br />
            Each project captures the VM inventory and produces an auditable BOM.
          </p>
          <button
            type="button"
            style={{ ...s.emptyBtn, opacity: creating ? 0.8 : 1 }}
            data-primary-cta
            onClick={handleNewProject}
            disabled={creating}
          >
            {creating ? (
              <><span style={s.spinner} /> Creating…</>
            ) : '+ New Project'}
          </button>
        </div>
      )}

      {!loading && projects.length > 0 && (
        <div style={s.grid}>
          {projects.map(p => (
            <div
              key={p.id}
              style={{ ...s.card, ...(hoveredId === p.id ? s.cardHover : {}) }}
              onMouseEnter={() => setHoveredId(p.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => { if (!loadingId && !deletingId) void handleOpenProject(p.id); }}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') void handleOpenProject(p.id); }}
              aria-label={`Open project ${p.name}`}
            >
              <p style={s.cardName}>{p.name}</p>
              <span style={s.cardClient}>{p.clientName}</span>
              {p.description && (
                <p style={s.cardDescription}>{p.description}</p>
              )}
              <div style={s.cardMeta}>
                <span style={s.cardDate}>Updated {formatDate(p.updatedAt)}</span>
                <div style={s.cardActions} onClick={e => e.stopPropagation()} role="presentation">
                  <button
                    type="button"
                    style={s.btnOpen}
                    onClick={() => void handleOpenProject(p.id)}
                    disabled={loadingId === p.id}
                    aria-label={`Open ${p.name}`}
                  >
                    {loadingId === p.id ? '…' : 'Open'}
                  </button>
                  <button
                    type="button"
                    style={s.btnDuplicate}
                    onClick={e => handleDuplicateClick(e, p.id, p.name)}
                    disabled={duplicatingId === p.id || !!loadingId}
                    aria-label={`Duplicate ${p.name}`}
                  >
                    {duplicatingId === p.id ? '…' : 'Duplicate'}
                  </button>
                  <button
                    type="button"
                    style={s.btnDelete}
                    onClick={e => handleDeleteClick(e, p.id)}
                    disabled={deletingId === p.id}
                    aria-label={`Delete ${p.name}`}
                  >
                    {deletingId === p.id ? '…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Duplicate dialog */}
      {duplicateSourceId && (
        <div style={s.overlay} role="dialog" aria-modal="true" aria-labelledby="duplicate-dialog-title">
          <div style={s.dialog}>
            <p id="duplicate-dialog-title" style={s.dialogTitle}>Duplicate project</p>
            <p style={s.dialogBody}>Enter a name for the new project.</p>
            <input
              style={s.input}
              type="text"
              value={duplicateName}
              onChange={e => setDuplicateName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void handleConfirmDuplicate(); }}
              aria-label="New project name"
              autoFocus
            />
            <div style={s.dialogActions}>
              <button
                type="button"
                style={s.btnCancel}
                onClick={() => setDuplicateSourceId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={s.btnConfirmDuplicate}
                onClick={() => void handleConfirmDuplicate()}
                disabled={!duplicateName.trim()}
              >
                Duplicate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation dialog */}
      {confirmDeleteId && (() => {
        const project = projects.find(p => p.id === confirmDeleteId);
        return (
          <div style={s.overlay} role="dialog" aria-modal="true" aria-labelledby="confirm-delete-title">
            <div style={s.dialog}>
              <p id="confirm-delete-title" style={s.dialogTitle}>Delete project?</p>
              <p style={s.dialogBody}>
                <strong style={{ color: '#E8EDF2' }}>{project?.name}</strong> will be permanently deleted
                along with all its applications, VMs, and scenarios. This cannot be undone.
              </p>
              <div style={s.dialogActions}>
                <button
                  type="button"
                  style={s.btnCancel}
                  onClick={() => setConfirmDeleteId(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  style={s.btnConfirmDelete}
                  onClick={() => void handleConfirmDelete()}
                  autoFocus
                >
                  Delete project
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
